import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { MapPin, Plus, Edit, Trash2, Save, Loader2, Store, Search, Clock, Star, Palette, ImageIcon, Upload, X } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";
import LocationLimitBanner from "../LocationLimitBanner";

const US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HT)" },
  { value: "America/Phoenix", label: "Arizona (MST)" },
  { value: "America/Indiana/Indianapolis", label: "Indiana (ET)" },
  { value: "America/Detroit", label: "Michigan (ET)" },
  { value: "America/Kentucky/Louisville", label: "Kentucky (ET/CT)" },
  { value: "America/Boise", label: "Boise (MT)" },
  { value: "America/Juneau", label: "Juneau (AKT)" },
  { value: "America/Adak", label: "Adak (HAT)" },
  { value: "Pacific/Guam", label: "Guam (ChST)" },
  { value: "America/Puerto_Rico", label: "Puerto Rico (AST)" },
  { value: "Pacific/Samoa", label: "Samoa (SST)" },
];

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const StoreLocationsTab = ({ isDark, headers }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", address: "", city: "", state: "", zip_code: "", phone: "", timezone: "", time_format: "", default_jobs_view: "", active: true, industry: "", tax_rate: "", primary_color: "", accent_color: "", business_name: "", business_email: "" });
  // Track the location's uploaded logo separately — we keep the filename
  // (so we know whether to render a preview) and the most recent logo URL
  // (cache-busted with ?v=… on upload). null = no logo on file.
  const [locLogo, setLocLogo] = useState({ filename: "", url: "" });
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef(null);
  const [limitStatus, setLimitStatus] = useState(null);

  // Address autocomplete state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => { fetchLocations(); fetchLimitStatus(); }, []);

  const fetchLimitStatus = async () => {
    try {
      const res = await axios.get(`${API}/store/location-limit-status`, { headers });
      setLimitStatus(res.data);
    } catch { /* non-fatal */ }
  };

  const fetchLocations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/store/locations`, { headers });
      setLocations(res.data.locations || []);
    } catch { toast.error("Failed to load locations"); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: "", code: "", address: "", city: "", state: "", zip_code: "", phone: "", timezone: "", time_format: "", default_jobs_view: "", active: true, industry: "", tax_rate: "", primary_color: "", accent_color: "", business_name: "", business_email: "" });
    setLocLogo({ filename: "", url: "" });
    setSuggestions([]);
    setShowSuggestions(false);
    setShowForm(true);
  };

  const openEdit = (loc) => {
    setEditingId(loc.location_id);
    setForm({
      name: loc.name || "", code: loc.code || "",
      address: loc.address || "", city: loc.city || "", state: loc.state || "", zip_code: loc.zip_code || "",
      phone: loc.phone || "", timezone: loc.timezone || "", time_format: loc.time_format || "", default_jobs_view: loc.default_jobs_view || "", active: loc.active !== false,
      industry: loc.industry || "",
      tax_rate: loc.tax_rate != null ? String(loc.tax_rate) : "",
      charge_tax: loc.charge_tax === true ? "yes" : loc.charge_tax === false ? "no" : "",
      primary_color: loc.primary_color || "",
      accent_color: loc.accent_color || "",
      business_name: loc.business_name || "",
      business_email: loc.business_email || "",
    });
    setLocLogo({
      filename: loc.logo_file || "",
      url: loc.logo_file ? `${API}/invoices/template/location-logo/${loc.location_id}?v=${Date.now()}` : "",
    });
    setSuggestions([]);
    setShowSuggestions(false);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Location name is required"); return; }
    setSaving(true);
    // Cast tax_rate to a number (or null) before sending. Backend expects
    // `Optional[float]`; an empty string would 422. Same for colors — empty
    // string clears the override; hex strings persist as-is.
    const payload = {
      ...form,
      industry: form.industry || null,
      tax_rate: form.tax_rate !== "" && form.tax_rate != null
        ? parseFloat(form.tax_rate)
        : null,
      charge_tax: form.charge_tax === "yes" ? true : form.charge_tax === "no" ? false : null,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/store/locations/${editingId}`, payload, { headers });
        toast.success("Location updated");
      } else {
        await axios.post(`${API}/store/locations`, payload, { headers });
        toast.success("Location created");
      }
      setShowForm(false);
      fetchLocations();
      fetchLimitStatus();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "LOCATION_LIMIT_REACHED") {
        toast.error(detail.message || "You've reached your plan's location limit. Upgrade to add more locations.", { duration: 6000 });
        setShowForm(false);
        fetchLimitStatus();
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to save");
      }
    }
    finally { setSaving(false); }
  };

  // ── Per-location logo upload / delete ──────────────────────────────
  // Upload posts multipart/form-data to /api/invoices/template/location-logo/{id}
  // and persists `logo_file` on the store_locations doc. The endpoint accepts
  // PNG/JPG/WebP/SVG up to 5MB. Delete calls the DELETE counterpart.
  const handleLogoFile = async (file) => {
    if (!file || !editingId) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be 5MB or smaller"); return; }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/invoices/template/location-logo/${editingId}`,
        fd,
        { headers: { ...headers, "Content-Type": "multipart/form-data" } },
      );
      setLocLogo({
        filename: r.data?.filename || "logo",
        url: `${API}/invoices/template/location-logo/${editingId}?v=${Date.now()}`,
      });
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to upload logo");
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };

  const removeLocLogo = async () => {
    if (!editingId || !locLogo.filename) return;
    try {
      await axios.delete(`${API}/invoices/template/location-logo/${editingId}`, { headers });
      setLocLogo({ filename: "", url: "" });
      toast.success("Logo removed");
    } catch (e) {
      toast.error("Failed to remove logo");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this location?")) return;
    try {
      await axios.delete(`${API}/store/locations/${id}`, { headers });
      toast.success("Location deleted");
      fetchLocations();
      fetchLimitStatus();
    } catch { toast.error("Failed to delete"); }
  };

  const setDefaultLocation = async (id) => {
    try {
      await axios.post(`${API}/store/locations/${id}/set-default`, {}, { headers });
      toast.success("Default location updated");
      fetchLocations();
    } catch { toast.error("Failed to set default"); }
  };

  // Address autocomplete
  const handleAddressInput = useCallback((value) => {
    setForm(prev => ({ ...prev, address: value }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 3) { setSuggestions([]); setShowSuggestions(false); return; }
    debounceRef.current = setTimeout(async () => {
      setAddrLoading(true);
      try {
        const res = await axios.get(`${API}/places/autocomplete?input=${encodeURIComponent(value)}`, getAuthConfig());
        setSuggestions(res.data.predictions || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      } finally { setAddrLoading(false); }
    }, 300);
  }, []);

  const selectSuggestion = async (suggestion) => {
    setShowSuggestions(false);
    setSuggestions([]);
    const addr = suggestion.description || "";
    setForm(prev => ({ ...prev, address: addr }));

    if (suggestion.place_id && suggestion.place_id !== "manual") {
      try {
        const res = await axios.get(`${API}/places/details/${suggestion.place_id}`, getAuthConfig());
        const d = res.data;
        setForm(prev => ({
          ...prev,
          address: d.street || d.formatted_address || addr,
          city: d.city || prev.city,
          state: d.state || prev.state,
          zip_code: d.zip || prev.zip_code,
        }));
        // Auto-detect timezone from state
        const tz = guessTimezone(d.state);
        if (tz) setForm(prev => ({ ...prev, timezone: prev.timezone || tz }));
      } catch {
        const zipMatch = addr.match(/\b(\d{5}(?:-\d{4})?)\b/);
        if (zipMatch) setForm(prev => ({ ...prev, zip_code: zipMatch[1] }));
      }
    }
  };

  // Guess timezone from US state abbreviation
  const guessTimezone = (state) => {
    if (!state) return null;
    const s = state.toUpperCase();
    const eastern = ["CT","DE","FL","GA","IN","KY","ME","MD","MA","MI","NH","NJ","NY","NC","OH","PA","RI","SC","TN","VT","VA","WV","DC"];
    const central = ["AL","AR","IL","IA","KS","LA","MN","MS","MO","NE","ND","OK","SD","TX","WI"];
    const mountain = ["AZ","CO","ID","MT","NM","UT","WY"];
    const pacific = ["CA","NV","OR","WA"];
    if (eastern.includes(s)) return "America/New_York";
    if (central.includes(s)) return "America/Chicago";
    if (mountain.includes(s)) return s === "AZ" ? "America/Phoenix" : "America/Denver";
    if (pacific.includes(s)) return "America/Los_Angeles";
    if (s === "AK") return "America/Anchorage";
    if (s === "HI") return "Pacific/Honolulu";
    return null;
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const canAddLocation = !limitStatus || limitStatus.allowed;
  const graceRestricted = limitStatus && limitStatus.over_limit && !limitStatus.in_grace;
  const addDisabled = !canAddLocation; // includes both at-limit and grace-expired

  return (
    <div className="space-y-4" data-testid="store-locations-tab">
      {limitStatus && <LocationLimitBanner status={limitStatus} />}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Store Locations</h3>
          <p className="text-sm text-muted-foreground">
            Manage your store locations for job assignments and inventory
            {limitStatus && !limitStatus.unlimited && (
              <span className="ml-2 text-xs font-medium">
                ({limitStatus.current}/{limitStatus.limit} used)
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={openCreate}
          disabled={addDisabled}
          className={addDisabled ? "bg-muted text-muted-foreground cursor-not-allowed" : "bg-[#0046BE] text-white"}
          data-testid="add-location-btn"
          title={graceRestricted ? "Blocked — grace period expired" : (addDisabled ? "Location limit reached — upgrade your plan" : "")}
        >
          <Plus className="w-4 h-4 mr-2" /> Add Location
        </Button>
      </div>

      {locations.length === 0 ? (
        <Card className={isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-gray-100"}>
          <CardContent className="p-8 text-center">
            <Store className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">No locations yet</p>
            <p className="text-sm text-muted-foreground/70 mt-1">Add your first store location to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {locations.map((loc) => (
            <Card key={loc.location_id} className={`${isDark ? "bg-zinc-900 border-zinc-800" : "bg-white border-gray-100 shadow-sm"} ${!loc.active ? "opacity-50" : ""} ${loc.is_default ? "ring-1 ring-amber-400/40" : ""}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-blue-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-foreground font-medium truncate">{loc.name}</h4>
                    {loc.code && <Badge variant="outline" className="text-[10px] border-theme">{loc.code}</Badge>}
                    {!loc.active && <Badge className="bg-red-500/10 text-red-400 text-[10px]">Inactive</Badge>}
                    {loc.is_default && (
                      <Badge className="bg-amber-400/15 text-amber-600 dark:text-amber-400 text-[10px] border border-amber-400/30 flex items-center gap-1" data-testid={`default-badge-${loc.location_id}`}>
                        <Star className="w-2.5 h-2.5 fill-current" /> Default
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {[loc.address, loc.city, loc.state, loc.zip_code].filter(Boolean).join(", ") || "No address"}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!loc.is_default && loc.active !== false && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-amber-500"
                      onClick={() => setDefaultLocation(loc.location_id)}
                      title="Set as default"
                      data-testid={`set-default-loc-${loc.location_id}`}
                    >
                      <Star className="w-4 h-4" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => openEdit(loc)} data-testid={`edit-loc-${loc.location_id}`}>
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => remove(loc.location_id)} data-testid={`delete-loc-${loc.location_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-background border-theme max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground">{editingId ? "Edit Location" : "New Location"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Location Name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-9" placeholder="Main Store" data-testid="loc-name-input" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Store Code</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="mt-1 h-9" placeholder="MAIN" maxLength={10} />
              </div>
            </div>

            {/* Address with autocomplete */}
            <div className="relative">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                <Search className="w-3 h-3" /> Address
              </Label>
              <Input
                value={form.address}
                onChange={(e) => handleAddressInput(e.target.value)}
                onFocus={() => { if (suggestions.length) setShowSuggestions(true); }}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                className="mt-1 h-9"
                placeholder="Start typing an address..."
                data-testid="loc-address-input"
              />
              {addrLoading && <Loader2 className="absolute right-3 top-[30px] w-3.5 h-3.5 animate-spin text-muted-foreground" />}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-lg shadow-lg max-h-[200px] overflow-y-auto overscroll-contain touch-pan-y" data-testid="address-suggestions">
                  {suggestions.map((s, i) => (
                    <button
                      key={s.place_id || i}
                      className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted/50 flex items-center gap-2 transition-colors"
                      onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                    >
                      <MapPin className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                      <span className="truncate">{s.description}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">City</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 h-9" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">State</Label>
                <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="mt-1 h-9" maxLength={2} placeholder="FL" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">ZIP</Label>
                <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} className="mt-1 h-9" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-9" placeholder="305-555-0001" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Timezone
                </Label>
                <Select value={form.timezone || "__none"} onValueChange={(v) => setForm({ ...form, timezone: v === "__none" ? "" : v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="loc-timezone-select">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">No timezone set</SelectItem>
                    {US_TIMEZONES.map(tz => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Time Format
                </Label>
                <Select value={form.time_format || "__inherit"} onValueChange={(v) => setForm({ ...form, time_format: v === "__inherit" ? "" : v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="loc-time-format-select">
                    <SelectValue placeholder="Inherit account setting" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__inherit">Inherit account setting</SelectItem>
                    <SelectItem value="12h">12-hour (1:00 PM)</SelectItem>
                    <SelectItem value="24h">24-hour (13:00)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Default Jobs View
                </Label>
                <Select value={form.default_jobs_view || "__inherit"} onValueChange={(v) => setForm({ ...form, default_jobs_view: v === "__inherit" ? "" : v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="loc-default-jobs-view-select">
                    <SelectValue placeholder="Inherit account setting" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__inherit">Inherit account setting</SelectItem>
                    <SelectItem value="calendar">Calendar</SelectItem>
                    <SelectItem value="timeline">Timeline</SelectItem>
                    <SelectItem value="list">List</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Industry + per-location tax — both Optional. Industry is
                purely informational today; it'll drive auto-seeded
                Service Categories / Job Types when the location is
                created (see backend store router). Tax rate, if set,
                overrides the account-wide rate when this location is
                picked on an invoice/estimate. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Industry</Label>
                <Select
                  value={form.industry || "__none"}
                  onValueChange={(v) => setForm({ ...form, industry: v === "__none" ? "" : v })}
                >
                  <SelectTrigger className="mt-1 h-9" data-testid="loc-industry-select">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— None —</SelectItem>
                    <SelectItem value="locksmith">Locksmith</SelectItem>
                    <SelectItem value="garage_door">Garage Door</SelectItem>
                    <SelectItem value="hvac">HVAC</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="carpet_cleaning">Carpet Cleaning</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="appliance_repair">Appliance Repair</SelectItem>
                    <SelectItem value="pest_control">Pest Control</SelectItem>
                    <SelectItem value="general">General / Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Tax Rate (%)</Label>
                <Input
                  value={form.tax_rate}
                  onChange={(e) => setForm({ ...form, tax_rate: e.target.value.replace(/[^0-9.]/g, "") })}
                  className="mt-1 h-9"
                  placeholder="e.g. 8.25"
                  inputMode="decimal"
                  data-testid="loc-tax-rate-input"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Charge Tax (Automatic)</Label>
                <Select value={form.charge_tax || "inherit"} onValueChange={(v) => setForm({ ...form, charge_tax: v === "inherit" ? "" : v })}>
                  <SelectTrigger className="mt-1 h-9" data-testid="loc-charge-tax-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Inherit business setting</SelectItem>
                    <SelectItem value="yes">Yes — charge tax</SelectItem>
                    <SelectItem value="no">No — don't charge tax</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <Label className="text-sm text-foreground">Active</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>

            {/* ── Per-location branding section.
                  Business identity (name + email) and colors are available
                  for both create AND edit so brand-new locations carry the
                  right branding from their very first invoice. Logo upload
                  needs an existing location_id, so that subsection is
                  gated by `editingId`. */}
            <div className="border border-theme/60 rounded-lg p-3 mt-2 space-y-3" data-testid="location-branding-section">
              <div className="flex items-center gap-2">
                <Palette className="w-4 h-4 text-amber-500" />
                <h4 className="text-sm font-semibold text-foreground font-['Manrope']">Per-location branding</h4>
              </div>
              <p className="text-[11px] text-muted-foreground -mt-1">
                Overrides on invoices/estimates scoped to this location. Empty fields inherit your business defaults.
              </p>

              {/* Business identity overrides (name + email shown on PDFs + emails) */}
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <Label className="text-[11px] text-muted-foreground">Business name on PDF / emails</Label>
                  <Input
                    value={form.business_name}
                    onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                    placeholder="Inherits HQ business name"
                    className="h-8 text-sm mt-1"
                    data-testid="loc-business-name-input"
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-muted-foreground">Business email on PDF / emails (Reply-To)</Label>
                  <Input
                    type="email"
                    value={form.business_email}
                    onChange={(e) => setForm({ ...form, business_email: e.target.value })}
                    placeholder="Inherits HQ business email"
                    className="h-8 text-sm mt-1"
                    data-testid="loc-business-email-input"
                  />
                </div>
              </div>

              {/* Color pickers — primary + accent — available for both create + edit */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "primary_color", label: "Primary color", placeholder: "#0046BE" },
                  { key: "accent_color", label: "Accent color", placeholder: "#FFCC00" },
                ].map(({ key, label, placeholder }) => {
                  const val = form[key] || "";
                  const display = /^#[0-9a-fA-F]{6}$/.test(val) ? val : placeholder;
                  return (
                    <div key={key}>
                      <Label className="text-[11px] text-muted-foreground">{label}</Label>
                      <div className="mt-1 flex items-center gap-2">
                        <input
                          type="color"
                          value={display}
                          onChange={(e) => setForm({ ...form, [key]: e.target.value.toUpperCase() })}
                          className="h-8 w-10 rounded border border-theme cursor-pointer bg-transparent p-0.5"
                          data-testid={`loc-${key.replace('_color','-color')}-picker`}
                        />
                        <Input
                          value={val}
                          onChange={(e) => setForm({ ...form, [key]: e.target.value.toUpperCase() })}
                          placeholder={placeholder}
                          maxLength={7}
                          className="h-8 text-xs flex-1 font-mono"
                          data-testid={`loc-${key.replace('_color','-color')}-hex`}
                        />
                        {val && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => setForm({ ...form, [key]: "" })}
                            className="h-8 w-8 text-muted-foreground"
                            data-testid={`loc-${key.replace('_color','-color')}-clear`}
                            title="Inherit from business defaults"
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Logo upload — only available after the location exists. */}
              {editingId ? (
                <div>
                  <Label className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <ImageIcon className="w-3 h-3" /> Location logo
                  </Label>
                  <div className="mt-1.5 flex items-center gap-3">
                    {locLogo.url ? (
                      <img
                        src={locLogo.url}
                        alt="Location logo"
                        className="h-12 w-auto max-w-[120px] object-contain border border-theme/60 rounded bg-white p-1"
                        data-testid="loc-logo-preview"
                      />
                    ) : (
                      <div className="h-12 w-20 flex items-center justify-center border border-dashed border-theme rounded text-[10px] text-muted-foreground">
                        No logo
                      </div>
                    )}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={(e) => handleLogoFile(e.target.files?.[0])}
                      data-testid="loc-logo-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={logoUploading}
                      className="h-8 px-3 text-xs"
                      data-testid="loc-logo-upload-btn"
                    >
                      {logoUploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                      {locLogo.filename ? "Replace" : "Upload"}
                    </Button>
                    {locLogo.filename && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={removeLocLogo}
                        className="h-8 px-2 text-xs text-muted-foreground hover:text-red-500"
                        data-testid="loc-logo-remove-btn"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">PNG / JPG / WebP / SVG · up to 5MB</p>
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground italic" data-testid="loc-logo-create-hint">
                  Save the location first, then upload a logo.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#0046BE] text-white" disabled={saving} data-testid="save-location-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreLocationsTab;
